import "./PushPolicySelector.css";

import { IChangeRequestService, changeRequestService, errorMessage } from "../services/ChangeRequestService";
import { useCallback, useEffect, useState } from "react";

import { PushPolicy } from "../models/ChangeRequestDTO";

interface IPushPolicySelectorProps {
    spaceId: string;
    service?: IChangeRequestService;
}

const hints: Record<PushPolicy, string> = {
    direct: "Changes pushed from the orbital CLI or Ark Studio are applied at once.",
    propose: "Every change pushed to this space waits here as a change request until someone approves it.",
};

/** The space's push policy (`PATCH api/agentic/v1/spaces/{id}`): `direct` applies pushes, `propose` parks them for review. */
export function PushPolicySelector(props: IPushPolicySelectorProps) {
    const service = props.service ?? changeRequestService();
    const [policy, setPolicy] = useState<PushPolicy | undefined>();
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        let alive = true;
        setError(undefined);
        service
            .getSpace(props.spaceId)
            .then((space) => alive && setPolicy(space.pushPolicy === "propose" ? "propose" : "direct"))
            .catch((err) => alive && setError(errorMessage(err)));
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.spaceId]);

    const handleChange = useCallback(
        (ev: React.ChangeEvent<HTMLSelectElement>) => {
            const next = ev.target.value as PushPolicy;
            setIsSaving(true);
            setError(undefined);
            setSaved(false);
            service
                .setPushPolicy(props.spaceId, next)
                .then((space) => {
                    setPolicy(space.pushPolicy === "propose" ? "propose" : "direct");
                    setSaved(true);
                    setIsSaving(false);
                })
                .catch((err) => {
                    setError(errorMessage(err));
                    setIsSaving(false);
                });
        },
        [service, props.spaceId]
    );

    const selectId = `push-policy-${props.spaceId}`;

    return (
        <div className="push-policy-selector">
            <label htmlFor={selectId}>Push policy</label>
            <select id={selectId} value={policy ?? ""} onChange={handleChange} disabled={!policy || isSaving}>
                {!policy ? <option value="">…</option> : null}
                <option value="direct">direct</option>
                <option value="propose">propose</option>
            </select>
            {policy ? <span className="push-policy-hint">{hints[policy]}</span> : null}
            {saved && !error ? <span className="push-policy-saved">Saved.</span> : null}
            {error ? <span className="push-policy-error">{error}</span> : null}
        </div>
    );
}
